import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Skeleton,
  Grid,
  Chip,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  InputAdornment,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import HistoryIcon from '@mui/icons-material/History';
import FilterListIcon from '@mui/icons-material/FilterList';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { monitoramentoService } from '../../services/api';
import { ChatIAButton } from '../../components/ChatIA';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Documento {
  id: number;
  titulo: string;
  resumo: string;
  url_original: string;
  fonte: string;
  categoria: string;
  abrangencia: string;
  nivel_risco: string;
  atividade: string;
  impacto_saude_publica: string;
  tags: string[];
  data_publicacao: string;
  data_coleta: string;
  lido: boolean;
  notificado: boolean;
  processado?: boolean;
  relevancia?: number;
  total_registros: number;
}

interface AuditoriaItem {
  id: number;
  usuario_email: string;
  acao: string;
  detalhes_json: Record<string, unknown>;
  created_at: string;
}

interface Filters {
  categoria: string;
  fonte: string;
  nivel_risco: string;
  abrangencia: string;
  atividade: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CORES_CATEGORIA: Record<string, string> = {
  LEGISLACAO: '#1E3A5F',
  NORMA_TECNICA: '#2D7D9A',
  ALERTA_EPIDEMIOLOGICO: '#E74C3C',
  RUMOR: '#F39C12',
  NOTICIA: '#27AE60',
};

const CORES_RISCO: Record<string, string> = {
  CRITICO: '#E74C3C',
  ALTO: '#F39C12',
  MEDIO: '#F1C40F',
  BAIXO: '#27AE60',
};

const LABEL_CATEGORIA: Record<string, string> = {
  LEGISLACAO: 'Legislacao',
  NORMA_TECNICA: 'Norma Tecnica',
  ALERTA_EPIDEMIOLOGICO: 'Alerta Epidemiologico',
  RUMOR: 'Rumor',
  NOTICIA: 'Noticia',
};

const LABEL_FONTE: Record<string, string> = {
  ANVISA: 'ANVISA',
  DOU: 'DOU',
  MS: 'Ministerio da Saude',
  OPAS: 'OPAS',
  FDA: 'FDA',
  GOOGLE_TRENDS: 'Google Trends',
  GOOGLE_NEWS: 'Google News',
  CIEVS: 'CIEVS',
};

const LABEL_RISCO: Record<string, string> = {
  CRITICO: 'Critico',
  ALTO: 'Alto',
  MEDIO: 'Medio',
  BAIXO: 'Baixo',
};

const LABEL_ABRANGENCIA: Record<string, string> = {
  INTERNACIONAL: 'Internacional',
  NACIONAL: 'Nacional',
  ESTADUAL: 'Estadual',
  MUNICIPAL: 'Municipal',
};

const LABEL_ATIVIDADE: Record<string, string> = {
  ALIMENTOS: 'Alimentos',
  MEDICAMENTOS: 'Medicamentos',
  COSMETICOS: 'Cosmeticos',
  SANEANTES: 'Saneantes',
  AGROTOXICOS: 'Agrotoxicos',
  GERAL: 'Geral',
};

const INITIAL_FILTERS: Filters = {
  categoria: '',
  fonte: '',
  nivel_risco: '',
  abrangencia: '',
  atividade: '',
};

const DEBOUNCE_MS = 500;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MonitoramentoVisaWebDetalhes: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const navigate = useNavigate();

  // Data state
  const [data, setData] = useState<Documento[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filters
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  // Search with debounce
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDestino, setEmailDestino] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState<string | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditoria, setAuditoria] = useState<AuditoriaItem[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // Debounce logic
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm]);

  // ---------------------------------------------------------------------------
  // Build filter params for the API call
  // ---------------------------------------------------------------------------

  const buildFilterParams = useCallback((): Record<string, any> => {
    const params: Record<string, any> = {};
    if (filters.categoria) params.categoria = filters.categoria;
    if (filters.fonte) params.fonte = filters.fonte;
    if (filters.nivel_risco) params.nivel_risco = filters.nivel_risco;
    if (filters.abrangencia) params.abrangencia = filters.abrangencia;
    if (filters.atividade) params.atividade = filters.atividade;
    if (debouncedSearch.trim()) params.busca = debouncedSearch.trim();
    params.page = page + 1; // API is 1-indexed
    params.limit = rowsPerPage;
    return params;
  }, [filters, debouncedSearch, page, rowsPerPage]);

  // ---------------------------------------------------------------------------
  // Fetch data
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = buildFilterParams();
        const result = await monitoramentoService.getVisaWebDocumentos(
          municipioAtivo.id,
          params,
        );

        if (cancelled) return;

        // The API may return { documentos: [...], total: N } or an array directly
        if (Array.isArray(result)) {
          setData(result);
          setTotalRegistros(result.length > 0 ? (result[0].total_registros ?? result.length) : 0);
        } else if (result?.documentos) {
          setData(result.documentos);
          setTotalRegistros(result.total ?? result.documentos.length);
        } else {
          setData([]);
          setTotalRegistros(0);
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
          'Erro ao carregar documentos VISA Web.',
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [municipioAtivo?.id, filters, page, rowsPerPage, debouncedSearch, buildFilterParams]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleFilterChange = (field: keyof Filters) => (
    event: SelectChangeEvent<string>,
  ) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSearchTerm('');
    setDebouncedSearch('');
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const buildExportParams = (): Record<string, string> => {
    const params: Record<string, string> = {};
    if (filters.categoria) params.categoria = filters.categoria;
    if (filters.fonte) params.fonte = filters.fonte;
    if (filters.nivel_risco) params.nivel_risco = filters.nivel_risco;
    if (filters.abrangencia) params.abrangencia = filters.abrangencia;
    if (filters.atividade) params.atividade = filters.atividade;
    if (debouncedSearch.trim()) params.busca = debouncedSearch.trim();
    return params;
  };

  const handleExportCsv = () => {
    if (!municipioAtivo?.id) return;
    const csvUrl = monitoramentoService.getVisaWebCsvUrl(municipioAtivo.id, buildExportParams());
    const baseUrl = process.env.REACT_APP_API_URL || '';
    window.open(`${baseUrl}${csvUrl}`, '_blank');
  };

  const handleExportPdf = () => {
    if (!municipioAtivo?.id) return;
    const pdfUrl = monitoramentoService.getVisaWebPdfUrl(
      municipioAtivo.id,
      municipioAtivo.nome || 'Municipio',
      buildExportParams(),
    );
    const baseUrl = process.env.REACT_APP_API_URL || '';
    window.open(`${baseUrl}${pdfUrl}`, '_blank');
  };

  const handleOpenAuditoria = async () => {
    if (!municipioAtivo?.id) return;
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const rows = await monitoramentoService.getVisaWebAuditoria(municipioAtivo.id);
      setAuditoria(Array.isArray(rows) ? rows : []);
    } catch {
      setAuditoria([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleEnviarEmail = async () => {
    if (!municipioAtivo?.id || !emailDestino.trim()) return;
    setEmailLoading(true);
    setEmailMsg(null);
    try {
      const res = await monitoramentoService.enviarVisaWebEmail(
        municipioAtivo.id,
        emailDestino.trim(),
        undefined,
        buildExportParams(),
      );
      setEmailMsg(res?.mensagem || 'E-mail enviado com sucesso.');
      setEmailDialogOpen(false);
      setEmailDestino('');
    } catch (err: any) {
      setEmailMsg(err?.response?.data?.message || 'Falha ao enviar e-mail.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/monitoramento/visa-web');
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const getCategoriaLabel = (cat: string): string =>
    LABEL_CATEGORIA[cat] || cat || '-';

  const getFonteLabel = (fonte: string): string =>
    LABEL_FONTE[fonte] || fonte || '-';

  const getRiscoLabel = (risco: string): string =>
    LABEL_RISCO[risco] || risco || '-';

  const getAbrangenciaLabel = (abr: string): string =>
    LABEL_ABRANGENCIA[abr] || abr || '-';

  const getAtividadeLabel = (atv: string): string =>
    LABEL_ATIVIDADE[atv] || atv || '-';

  const hasActiveFilters =
    filters.categoria !== '' ||
    filters.fonte !== '' ||
    filters.nivel_risco !== '' ||
    filters.abrangencia !== '' ||
    filters.atividade !== '' ||
    searchTerm.trim() !== '';

  // ---------------------------------------------------------------------------
  // Render: no municipio selected
  // ---------------------------------------------------------------------------

  if (!municipioAtivo) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          px: 3,
        }}
      >
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar os documentos coletados pela
          VISA Web.
        </Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: loading skeleton
  // ---------------------------------------------------------------------------

  if (loading && data.length === 0) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton
          variant="rectangular"
          height={56}
          sx={{ borderRadius: 2, mt: 2, mb: 2 }}
        />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={2} key={i}>
              <Skeleton
                variant="rectangular"
                height={56}
                sx={{ borderRadius: 1 }}
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton
          variant="rectangular"
          height={500}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: error
  // ---------------------------------------------------------------------------

  if (error && data.length === 0) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToDashboard}
            sx={{ mb: 2 }}
          >
            Voltar ao Dashboard
          </Button>
        </Box>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: main
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* ----------------------------------------------------------------- */}
      {/* Header                                                            */}
      {/* ----------------------------------------------------------------- */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            VISA — Documentos Coletados
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCsv} size="medium">
            Exportar CSV
          </Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf} size="medium">
            Exportar PDF
          </Button>
          <Button variant="outlined" startIcon={<EmailIcon />} onClick={() => setEmailDialogOpen(true)} size="medium">
            Enviar por E-mail
          </Button>
          <Button variant="outlined" startIcon={<HistoryIcon />} onClick={handleOpenAuditoria} size="medium">
            Auditoria
          </Button>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBackToDashboard} size="medium">
            Voltar ao Dashboard
          </Button>
        </Box>
      </Box>

      {/* ----------------------------------------------------------------- */}
      {/* Search bar                                                        */}
      {/* ----------------------------------------------------------------- */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar por titulo, resumo ou palavras-chave..."
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* ----------------------------------------------------------------- */}
      {/* Filter row                                                        */}
      {/* ----------------------------------------------------------------- */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
          }}
        >
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            Filtros
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          {/* Categoria */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-categoria-label">Categoria</InputLabel>
              <Select
                labelId="filter-categoria-label"
                value={filters.categoria}
                onChange={handleFilterChange('categoria')}
                label="Categoria"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="LEGISLACAO">Legislacao</MenuItem>
                <MenuItem value="NORMA_TECNICA">Norma Tecnica</MenuItem>
                <MenuItem value="ALERTA_EPIDEMIOLOGICO">
                  Alerta Epidemiologico
                </MenuItem>
                <MenuItem value="RUMOR">Rumor</MenuItem>
                <MenuItem value="NOTICIA">Noticia</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Fonte */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-fonte-label">Fonte</InputLabel>
              <Select
                labelId="filter-fonte-label"
                value={filters.fonte}
                onChange={handleFilterChange('fonte')}
                label="Fonte"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="ANVISA">ANVISA</MenuItem>
                <MenuItem value="DOU">DOU</MenuItem>
                <MenuItem value="MS">Ministerio da Saude</MenuItem>
                <MenuItem value="OPAS">OPAS</MenuItem>
                <MenuItem value="FDA">FDA</MenuItem>
                <MenuItem value="GOOGLE_TRENDS">Google Trends</MenuItem>
                <MenuItem value="GOOGLE_NEWS">Google News</MenuItem>
                <MenuItem value="CIEVS">CIEVS</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Nivel de Risco */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-risco-label">Nivel de Risco</InputLabel>
              <Select
                labelId="filter-risco-label"
                value={filters.nivel_risco}
                onChange={handleFilterChange('nivel_risco')}
                label="Nivel de Risco"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="CRITICO">Critico</MenuItem>
                <MenuItem value="ALTO">Alto</MenuItem>
                <MenuItem value="MEDIO">Medio</MenuItem>
                <MenuItem value="BAIXO">Baixo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Abrangencia */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-abrangencia-label">
                Abrangencia
              </InputLabel>
              <Select
                labelId="filter-abrangencia-label"
                value={filters.abrangencia}
                onChange={handleFilterChange('abrangencia')}
                label="Abrangencia"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="INTERNACIONAL">Internacional</MenuItem>
                <MenuItem value="NACIONAL">Nacional</MenuItem>
                <MenuItem value="ESTADUAL">Estadual</MenuItem>
                <MenuItem value="MUNICIPAL">Municipal</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Atividade */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-atividade-label">Atividade</InputLabel>
              <Select
                labelId="filter-atividade-label"
                value={filters.atividade}
                onChange={handleFilterChange('atividade')}
                label="Atividade"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="ALIMENTOS">Alimentos</MenuItem>
                <MenuItem value="MEDICAMENTOS">Medicamentos</MenuItem>
                <MenuItem value="COSMETICOS">Cosmeticos</MenuItem>
                <MenuItem value="SANEANTES">Saneantes</MenuItem>
                <MenuItem value="AGROTOXICOS">Agrotoxicos</MenuItem>
                <MenuItem value="GERAL">Geral</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Limpar Filtros */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="text"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              fullWidth
              sx={{ height: 40 }}
            >
              Limpar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ----------------------------------------------------------------- */}
      {/* Inline error (when data exists but refresh failed)                */}
      {/* ----------------------------------------------------------------- */}
      {error && data.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {emailMsg && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setEmailMsg(null)}>
          {emailMsg}
        </Alert>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Results count                                                     */}
      {/* ----------------------------------------------------------------- */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Mostrando{' '}
          <strong>
            {data.length}
          </strong>{' '}
          de{' '}
          <strong>
            {totalRegistros.toLocaleString('pt-BR')}
          </strong>{' '}
          documentos
        </Typography>
        {loading && (
          <Skeleton
            variant="circular"
            width={18}
            height={18}
            sx={{ ml: 0.5 }}
          />
        )}
      </Box>

      {/* ----------------------------------------------------------------- */}
      {/* Documents table                                                   */}
      {/* ----------------------------------------------------------------- */}
      <TableContainer
        component={Paper}
        elevation={1}
        sx={{ borderRadius: 2, mb: 0 }}
      >
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: 'rgba(30, 58, 95, 0.05)',
              }}
            >
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 100 }}>
                Data
              </TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 280 }}>
                Titulo / Impacto
              </TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 160 }}>
                Categoria
              </TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 120 }}>
                Fonte
              </TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 100 }}>
                Risco
              </TableCell>
              {debouncedSearch.trim() && (
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 80 }}>
                  Relev.
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 90 }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 60 }} align="center">
                Link
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={debouncedSearch.trim() ? 9 : 8} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    Nenhum documento encontrado para os filtros selecionados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((doc) => (
                <TableRow
                  key={doc.id}
                  hover
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    opacity: doc.lido ? 0.85 : 1,
                  }}
                >
                  {/* Data */}
                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    <Typography variant="body2">
                      {doc.data_publicacao || '-'}
                    </Typography>
                  </TableCell>

                  {/* Titulo + Resumo */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        mb: 0.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {doc.titulo || 'Sem titulo'}
                    </Typography>
                    {doc.resumo && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.8rem',
                          lineHeight: 1.4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {doc.resumo}
                      </Typography>
                    )}
                    {doc.impacto_saude_publica && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Impacto: {doc.impacto_saude_publica}
                      </Typography>
                    )}
                    {doc.tags && doc.tags.length > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {doc.tags.slice(0, 4).map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        ))}
                      </Box>
                    )}
                    {doc.data_coleta && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.3 }}>
                        Coleta: {doc.data_coleta}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Categoria */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {doc.categoria ? (
                      <Chip
                        label={getCategoriaLabel(doc.categoria)}
                        size="small"
                        sx={{
                          backgroundColor: CORES_CATEGORIA[doc.categoria] || '#757575',
                          color: '#fff',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>

                  {/* Fonte */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2">
                      {getFonteLabel(doc.fonte)}
                    </Typography>
                  </TableCell>

                  {/* Risco */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {doc.nivel_risco ? (
                      <Chip
                        label={getRiscoLabel(doc.nivel_risco)}
                        size="small"
                        sx={{
                          backgroundColor: CORES_RISCO[doc.nivel_risco] || '#757575',
                          color: doc.nivel_risco === 'MEDIO' ? '#333' : '#fff',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>

                  {debouncedSearch.trim() && (
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {doc.relevancia != null ? Number(doc.relevancia).toFixed(2) : '-'}
                      </Typography>
                    </TableCell>
                  )}

                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {doc.processado !== false && (
                        <Chip label="Auto" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                      {doc.notificado && (
                        <Chip icon={<NotificationsActiveIcon sx={{ fontSize: '14px !important' }} />} label="Notif." size="small" color="warning" sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Box>
                  </TableCell>

                  {/* Link */}
                  <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                    {doc.url_original ? (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          window.open(doc.url_original, '_blank', 'noopener,noreferrer')
                        }
                        title="Abrir documento original"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalRegistros}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Linhas por pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count.toLocaleString('pt-BR') : `mais de ${to}`}`
          }
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        />
      </TableContainer>

      {/* ----------------------------------------------------------------- */}
      {/* Chat IA FAB                                                       */}
      {/* ----------------------------------------------------------------- */}
      <Dialog open={emailDialogOpen} onClose={() => !emailLoading && setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar relatorio por e-mail</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="E-mail destino"
            type="email"
            value={emailDestino}
            onChange={(e) => setEmailDestino(e.target.value)}
            sx={{ mt: 1 }}
            placeholder="vigilancia.sanitaria@contagem.mg.gov.br"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            O relatorio incluira os filtros e busca ativos nesta listagem.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={emailLoading}>Cancelar</Button>
          <Button variant="contained" onClick={handleEnviarEmail} disabled={emailLoading || !emailDestino.trim()}>
            {emailLoading ? <CircularProgress size={22} /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={auditOpen} onClose={() => setAuditOpen(false)}>
        <Box sx={{ width: 380, p: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Historico de Auditoria
          </Typography>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : auditoria.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum registro de auditoria ainda. Exportacoes e envios de e-mail serao registrados aqui.
            </Typography>
          ) : (
            <List dense>
              {auditoria.map((a) => (
                <ListItem key={a.id} alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <ListItemText
                    primary={a.acao.replace(/_/g, ' ')}
                    secondary={
                      <>
                        <Typography variant="caption" component="span" display="block">
                          {a.created_at} {a.usuario_email ? `| ${a.usuario_email}` : ''}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify({
          painel: 'visa-web-detalhes',
          municipio: municipioAtivo.nome,
          totalDocumentos: totalRegistros,
          filtrosAtivos: filters,
          busca: debouncedSearch,
        })}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoVisaWebDetalhes;
